import {
  Room,
  RoomConnectOptions,
  Track,
  RemoteTrack,
  RemoteTrackPublication,
  RemoteParticipant,
} from 'livekit-client';
import { createApiClient } from '@/services/api/httpClient';

export interface LiveKitConnectionConfig {
  wsUrl: string;
  roomName: string;
  identity: string;
  clientId: string;
  connectOptions?: RoomConnectOptions;
}

export interface ParticipantTrack {
  trackId: string;
  participantId: string;
  participantIdentity: string;
  source: Track.Source;
  track: RemoteTrack;
  publication: RemoteTrackPublication;
  participant: RemoteParticipant;
}

export class RoomInstance {
  private room: Room;
  private config: LiveKitConnectionConfig;
  private token: string | null = null;
  private tracks = new Map<string, ParticipantTrack>();

  constructor(config: LiveKitConnectionConfig) {
    this.config = { ...config };
    this.room = new Room({
      adaptiveStream: true,
      dynacast: true,
      ...config.connectOptions,
    });

    this.setupTrackHandlers();
  }

  private setupTrackHandlers(): void {
    this.room.on(
      'trackPublished',
      (publication: RemoteTrackPublication, participant: RemoteParticipant) => {
        console.log(
          `Track published: ${publication.trackSid} from participant ${participant.identity}`,
        );
      },
    );

    this.room.on(
      'trackSubscribed',
      (
        track: RemoteTrack,
        publication: RemoteTrackPublication,
        participant: RemoteParticipant,
      ) => {
        const participantTrack: ParticipantTrack = {
          trackId: track.sid || publication.trackSid,
          participantId: participant.sid,
          participantIdentity: participant.identity,
          source: publication.source!,
          track,
          publication,
          participant, // Add the participant reference
        };

        this.tracks.set(participantTrack.trackId, participantTrack);
        console.log(
          `Track subscribed: ${participantTrack.trackId} from participant ${participant.identity}`,
        );
      },
    );

    this.room.on(
      'trackUnsubscribed',
      (
        track: RemoteTrack,
        publication: RemoteTrackPublication,
        participant: RemoteParticipant,
      ) => {
        const trackId = track.sid || publication.trackSid;
        this.tracks.delete(trackId);
        console.log(
          `Track unsubscribed: ${trackId} from participant ${participant.identity}`,
        );
      },
    );

    this.room.on(
      'participantDisconnected',
      (participant: RemoteParticipant) => {
        // Clean up tracks for disconnected participant
        for (const [trackId, participantTrack] of this.tracks.entries()) {
          if (participantTrack.participantId === participant.sid) {
            this.tracks.delete(trackId);
          }
        }
        console.log(`Participant disconnected: ${participant.identity}`);
      },
    );
  }

  private async fetchToken(): Promise<string> {
    try {
      console.log(`Fetching token for room: ${this.config.roomName}`);

      const apiClient = createApiClient();
      const endpoint = `/livekit/token?client_id=${this.config.clientId}&room=${this.config.roomName}&identity=${this.config.identity}`;

      const response = await apiClient.get<{ token: string }>(endpoint);

      this.token = response.token;
      return response.token;
    } catch (error) {
      console.error('Failed to fetch LiveKit token:', error);
      throw new Error(
        `Token fetch failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  async connect(): Promise<void> {
    if (this.room.state === 'connected' || this.room.state === 'connecting') {
      return;
    }

    try {
      if (!this.token) {
        await this.fetchToken();
      }

      if (!this.token) {
        throw new Error('Failed to obtain LiveKit token');
      }

      await this.room.connect(this.config.wsUrl, this.token);
    } catch (error) {
      console.error(
        `Failed to connect to room ${this.config.roomName}:`,
        error,
      );
      throw error;
    }
  }

  async disconnect(): Promise<void> {
    if (this.room.state === 'disconnected') {
      return;
    }

    await this.room.disconnect();
  }

  getTracks(): ParticipantTrack[] {
    return Array.from(this.tracks.values());
  }

  getTracksByParticipant(participantId: string): ParticipantTrack[] {
    return this.getTracks().filter(
      (track) => track.participantId === participantId,
    );
  }

  getVideoTracks(): ParticipantTrack[] {
    return this.getTracks().filter(
      (track) =>
        track.source === Track.Source.Camera ||
        track.source === Track.Source.ScreenShare,
    );
  }

  getAudioTracks(): ParticipantTrack[] {
    return this.getTracks().filter(
      (track) =>
        track.source === Track.Source.Microphone ||
        track.source === Track.Source.ScreenShareAudio,
    );
  }
}

export class LiveKitService {
  private roomInstances = new Map<string, RoomInstance>();

  private getRoomKey(roomName: string, identity: string): string {
    return `${roomName}:${identity}`;
  }

  createRoomInstance(config: LiveKitConnectionConfig): RoomInstance {
    const roomKey = this.getRoomKey(config.roomName, config.identity);

    // Destroy existing instance if it exists
    this.destroyRoomInstance(config.roomName, config.identity);

    // Create new instance
    const roomInstance = new RoomInstance(config);
    this.roomInstances.set(roomKey, roomInstance);

    return roomInstance;
  }

  getRoomInstance(
    roomName: string,
    identity: string,
  ): RoomInstance | undefined {
    const roomKey = this.getRoomKey(roomName, identity);
    return this.roomInstances.get(roomKey);
  }

  destroyRoomInstance(roomName: string, identity: string): void {
    const roomKey = this.getRoomKey(roomName, identity);
    const instance = this.roomInstances.get(roomKey);

    if (instance) {
      instance.disconnect().catch(console.error);
      this.roomInstances.delete(roomKey);
    }
  }

  getAllRoomInstances(): RoomInstance[] {
    return Array.from(this.roomInstances.values());
  }

  getConnectedRooms(): RoomInstance[] {
    return this.getAllRoomInstances().filter(
      (instance) => instance.getTracks().length > 0,
    );
  }

  destroyAll(): void {
    this.roomInstances.forEach((instance) => {
      instance.disconnect().catch(console.error);
    });
    this.roomInstances.clear();
  }
}

// Singleton factory instance
let liveKitServiceInstance: LiveKitService | null = null;

export function createLiveKitServiceInstance(): LiveKitService {
  if (!liveKitServiceInstance) {
    liveKitServiceInstance = new LiveKitService();
  }
  return liveKitServiceInstance;
}

export function getLiveKitServiceInstance(): LiveKitService {
  if (!liveKitServiceInstance) {
    liveKitServiceInstance = new LiveKitService();
  }
  return liveKitServiceInstance;
}
